import { useCallback, useState } from "react";
import useSWR from "swr";

import { api, TokenCreated, TokenInfo } from "../api";
import { AgentCallout, Icon, PageHeader } from "../ui";

const fetcher = (path: string) => api.get<TokenInfo[]>(path);

const EXPIRY_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 180, label: "6 months" },
  { value: 365, label: "1 year" },
];

export function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isExpired(expiresAt: string) {
  return new Date(expiresAt) < new Date();
}

// ── Confirm modal ────────────────────────────────────────────

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  variant: "danger" | "warning";
  onConfirm: () => Promise<void>;
}

function ConfirmModal({ state, onClose }: { state: ConfirmState; onClose: () => void }) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await state.onConfirm();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[400px] mx-4 flex flex-col gap-3 p-6 rounded-md bg-card border border-border animate-scale-in">
        <h3 className="text-sm font-semibold text-main">{state.title}</h3>
        <p className="text-xs text-muted">{state.message}</p>
        <div className="flex gap-2 mt-1">
          {state.variant === "danger" ? (
            <button
              className="btn-danger"
              disabled={busy}
              onClick={handleConfirm}
            >
              {busy ? "Processing…" : state.confirmLabel}
            </button>
          ) : (
            <button
              className="btn-primary"
              disabled={busy}
              onClick={handleConfirm}
            >
              {busy ? "Processing…" : state.confirmLabel}
            </button>
          )}
          <button className="btn-default" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Tokens page ──────────────────────────────────────────────

export function Tokens() {
  const { data, mutate } = useSWR<TokenInfo[]>("/tokens", fetcher);
  const [showCreate, setShowCreate] = useState(false);
  const [created, setCreated] = useState<TokenCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const copyToken = () => {
    if (created) {
      navigator.clipboard.writeText(created.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title="API tokens"
        description="Create and manage bearer tokens for authenticating API requests."
        action={
          !showCreate && (
            <button className="btn-primary" onClick={() => { setShowCreate(true); setCreated(null); }}>
              <Icon name="plus" className="w-4 h-4" />
              New token
            </button>
          )
        }
      />

      {/* New token banner */}
      {created && (
        <div className="flex flex-col gap-3.5 p-6 rounded-lg bg-card border border-accent">
          <h3 className="text-sm font-semibold text-main">Token created</h3>
          <p className="text-sm text-muted">
            Copy this token now — it will not be shown again.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={created.token}
              className="flex-1 px-3 py-2 rounded-md text-[13px] font-mono break-all outline-none bg-stat border border-border text-main"
            />
            <button
              className={copied ? "btn-sm bg-pos-bg border-pos text-pos" : "btn-sm btn-default"}
              onClick={copyToken}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <button className="btn-default self-start" onClick={() => setCreated(null)}>Done</button>
        </div>
      )}

      {showCreate && (
        <CreateTokenForm
          onCreated={(t) => { setCreated(t); setShowCreate(false); mutate(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {!data && <p className="text-sm text-muted">Loading…</p>}
      {data && data.length === 0 && (
        <p className="text-sm text-muted">No tokens yet. Create one to get started.</p>
      )}
      {data && data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="max-sm:hidden">Created</th>
                <th>Expires</th>
                <th className="max-md:hidden">Last used</th>
                <th>Status</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <TokenRow key={t.id} token={t} onChange={mutate} onConfirm={setConfirm} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AgentCallout />

      {confirm && <ConfirmModal state={confirm} onClose={() => setConfirm(null)} />}
    </section>
  );
}

// ── Token row ────────────────────────────────────────────────

function TokenRow({
  token,
  onChange,
  onConfirm,
}: {
  token: TokenInfo;
  onChange: () => void;
  onConfirm: (s: ConfirmState) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const expired = isExpired(token.expires_at);

  const revoke = useCallback(() => {
    onConfirm({
      title: "Revoke token",
      message: `Revoke "${token.name}"? The token will stop working immediately. This cannot be undone.`,
      confirmLabel: "Revoke",
      variant: "warning",
      onConfirm: async () => {
        setError(null);
        try {
          await api.post(`/tokens/${token.id}/revoke`);
          onChange();
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : "Failed to revoke token");
        }
      },
    });
  }, [token, onChange, onConfirm]);

  const del = useCallback(() => {
    onConfirm({
      title: "Delete token",
      message: `Permanently delete "${token.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        setError(null);
        try {
          await api.delete(`/tokens/${token.id}`);
          onChange();
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : "Failed to delete token");
        }
      },
    });
  }, [token, onChange, onConfirm]);

  return (
    <tr>
      <td className="text-main font-medium whitespace-nowrap">
        {token.name}
        {error && <p className="text-[13px] font-normal mt-1 text-neg">{error}</p>}
      </td>
      <td className="max-sm:hidden whitespace-nowrap">{formatDate(token.created_at)}</td>
      <td className="whitespace-nowrap">{formatDate(token.expires_at)}</td>
      <td className="max-md:hidden whitespace-nowrap">{token.last_used_at ? formatDate(token.last_used_at) : "Never"}</td>
      <td><TokenStatus token={token} /></td>
      <td>
        <div className="flex justify-end gap-1.5">
          {token.is_active && !expired && (
            <button className="btn-sm btn-default" onClick={revoke}>Revoke</button>
          )}
          <button className="btn-sm btn-danger" onClick={del}>Delete</button>
        </div>
      </td>
    </tr>
  );
}

export function TokenStatus({ token }: { token: TokenInfo }) {
  if (!token.is_active) return <span className="text-xs font-medium px-2 py-1 rounded-md bg-neg-bg text-neg">Revoked</span>;
  if (isExpired(token.expires_at)) return <span className="text-xs font-medium px-2 py-1 rounded-md bg-warning-bg text-warning">Expired</span>;
  return <span className="text-xs font-medium px-2 py-1 rounded-md bg-pos-bg text-pos">Active</span>;
}

// ── Create form ──────────────────────────────────────────────

function CreateTokenForm({
  onCreated,
  onCancel,
}: {
  onCreated: (t: TokenCreated) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState(180);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<TokenCreated>("/tokens", {
        name: name.trim(),
        expires_in_days: expiryDays,
      });
      onCreated(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create token");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3.5 p-6 rounded-lg bg-card border border-border" onSubmit={submit}>
      <h3 className="text-sm font-semibold text-main">New token</h3>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">Name</span>
        <input
          type="text"
          required
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. data-pipeline-prod"
          className="px-3 py-2 rounded-md text-sm font-sans outline-none transition-colors bg-input border border-input-border text-main focus:border-accent"
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">Expires in</span>
        <div className="flex gap-1.5 flex-wrap">
          {EXPIRY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`px-3 py-1.5 rounded-md text-xs font-medium font-sans cursor-pointer transition-colors border ${
                expiryDays === opt.value
                  ? "bg-accent border-accent text-white"
                  : "bg-input border-border text-muted hover:border-muted hover:text-main"
              }`}
              onClick={() => setExpiryDays(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-[13px] text-neg">{error}</p>}

      <div className="flex gap-2 mt-1">
        <button
          type="submit"
          className="btn-primary"
          disabled={busy || !name.trim()}
        >
          {busy ? "Creating…" : "Create"}
        </button>
        <button type="button" className="btn-default" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
